/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import { Button } from 'app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Input } from 'app/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';
import { Spinner } from 'app/components/ui/spinner';
import { TransactionBadge } from 'app/types/transaction';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { Check, Palette, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateBadge,
  useDeleteBadge,
  useTransactionBadges,
  useUpdateBadge
} from 'app/hooks/requests/bank-accounts/use-transaction-badges';
import { BadgeColorPicker } from './badge-color-picker';
import { BadgePill } from './badge-pill';
import { randomBadgeColor } from './badge-utils';

/**
 * `Props` type.
 */

type Props = {
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

/**
 * `BadgeRow` — one existing badge, editable inline (name + color) with delete.
 */

function BadgeRow({ badge }: { badge: TransactionBadge }): JSX.Element {
  const [translate] = useTranslation();
  const { error } = useToast();
  const update = useUpdateBadge();
  const remove = useDeleteBadge();
  const [name, setName] = useState(badge.name);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const saveName = () => {
    const next = name.trim();

    if (!next || next === badge.name) {
      setName(badge.name);

      return;
    }

    update.mutateAsync({ id: badge.id, name: next }).catch(() => {
      setName(badge.name);
      error({ message: translate('bankAccounts.movements.badges.saveFailed') });
    });
  };

  const saveColor = (color: string) => {
    update
      .mutateAsync({ id: badge.id, color })
      .catch(() => error({ message: translate('bankAccounts.movements.badges.saveFailed') }));
  };

  return (
    <div className={'flex items-center gap-2'}>
      <BadgePill color={badge.color}>{name || badge.name}</BadgePill>

      <Input
        className={'h-8 flex-1 text-sm'}
        onBlur={saveName}
        onChange={event => setName(event.target.value)}
        onKeyDown={event => event.key === 'Enter' && saveName()}
        value={name}
      />

      <Popover>
        <PopoverTrigger asChild>
          <Button aria-label={translate('bankAccounts.movements.badges.color')} size={'icon'} variant={'ghost'}>
            <Palette className={'h-4 w-4'} />
          </Button>
        </PopoverTrigger>

        <PopoverContent align={'end'} className={'w-auto p-2'}>
          <BadgeColorPicker onChange={saveColor} value={badge.color} />
        </PopoverContent>
      </Popover>

      <Button
        aria-label={translate('bankAccounts.movements.badges.delete')}
        className={'text-muted-foreground hover:text-destructive'}
        onClick={() => setConfirmOpen(true)}
        size={'icon'}
        variant={'ghost'}
      >
        <Trash2 className={'h-4 w-4'} />
      </Button>

      <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('bankAccounts.movements.badges.delete')}</AlertDialogTitle>

            <AlertDialogDescription>
              {translate('bankAccounts.movements.badges.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
              onClick={() => remove.mutate(badge.id)}
            >
              {translate('common.actions.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Export `ManageBadgesDialog` — the badge library (create / rename / recolor /
 * delete).
 */

export function ManageBadgesDialog({ onOpenChange, open }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { error, success } = useToast();
  const { data: badges, isLoading } = useTransactionBadges();
  const create = useCreateBadge();
  const [name, setName] = useState('');
  const [color, setColor] = useState(() => randomBadgeColor());

  const onCreate = async () => {
    const next = name.trim();

    if (!next) {
      return;
    }

    try {
      await create.mutateAsync({ color, name: next });
      success({ message: translate('bankAccounts.movements.badges.created') });
      setName('');
      setColor(randomBadgeColor(color));
    } catch (err) {
      error({ message: translate('bankAccounts.movements.badges.saveFailed') });
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className={'max-w-lg'}>
        <DialogHeader>
          <DialogTitle>{translate('bankAccounts.movements.badges.manageTitle')}</DialogTitle>
        </DialogHeader>

        {/* Create */}
        <div className={'space-y-2 rounded-lg border border-border p-3'}>
          <div className={'flex items-center gap-2'}>
            <Input
              className={'h-9 flex-1 text-sm'}
              onChange={event => setName(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && onCreate()}
              placeholder={translate('bankAccounts.movements.badges.namePlaceholder')}
              value={name}
            />

            <BadgePill color={color}>{name.trim() || translate('bankAccounts.movements.badges.preview')}</BadgePill>

            <Button disabled={!name.trim() || create.isLoading} onClick={onCreate}>
              <Check className={'h-4 w-4'} />

              {translate('common.actions.add')}
            </Button>
          </div>

          <BadgeColorPicker onChange={setColor} value={color} />
        </div>

        {/* Existing */}
        <div className={'max-h-[320px] space-y-2 overflow-y-auto'}>
          {isLoading ? (
            <div className={'grid place-items-center py-6'}>
              <Spinner />
            </div>
          ) : (badges ?? []).length === 0 ? (
            <p className={'py-6 text-center text-sm text-muted-foreground'}>
              {translate('bankAccounts.movements.badges.empty')}
            </p>
          ) : (
            (badges ?? []).map(badge => <BadgeRow badge={badge} key={badge.id} />)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
